import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const JadwalPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedCompetitionName, setSelectedCompetitionName] = useState('');
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/lapangbola/competitions`);
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        setCompetitions(response.data.data);
        const firstComp = response.data.data[0];
        setSelectedCompetition(firstComp.id);
        setSelectedCompetitionName(firstComp.name);
        fetchMatches(firstComp.id);
      } else {
        setCompetitions([]);
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
      toast.error('Gagal memuat data kompetisi');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async (competitionId) => {
    try {
      setLoadingMatches(true);
      const response = await axios.get(`${API}/api/lapangbola/matches`, {
        params: { competition_id: competitionId }
      });
      
      if (response.data && response.data.data) {
        // Filter upcoming matches (not finished)
        const upcomingMatches = response.data.data.filter(
          m => m.match_status !== 'Finished'
        );
        // Sort by date
        upcomingMatches.sort((a, b) => {
          const dateA = new Date(a.default_match_date || a.match_date);
          const dateB = new Date(b.default_match_date || b.match_date);
          return dateA - dateB;
        });
        setMatches(upcomingMatches);
      } else {
        setMatches([]);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
      toast.error('Gagal memuat jadwal pertandingan');
      setMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  const handleCompetitionChange = (competitionId) => {
    const comp = competitions.find(c => c.id === Number(competitionId));
    setSelectedCompetition(Number(competitionId));
    setSelectedCompetitionName(comp?.name || '');
    fetchMatches(Number(competitionId));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto"></div>
          <p className="mt-4 text-gray-600">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-ltb-r text-white p-4">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="mb-2 flex items-center text-white/80 hover:text-white"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Kembali
          </button>
          <h1 className="text-2xl font-bold">Jadwal Pertandingan</h1>
          <p className="text-white/80 text-sm mt-1">Jadwal pertandingan yang akan datang</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Competition Selector */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Pilih Kompetisi</label>
          <select
            value={selectedCompetition || ''}
            onChange={(e) => handleCompetitionChange(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          >
            {competitions.map(comp => (
              <option key={comp.id} value={comp.id}>{comp.name}</option>
            ))}
          </select>
        </div>

        {/* Matches List */}
        {loadingMatches ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto mb-4"></div>
            <p className="text-gray-600">Memuat jadwal...</p>
          </div>
        ) : matches.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-600 text-lg">Belum ada jadwal pertandingan</p>
            <p className="text-gray-400 text-sm mt-2">Jadwal akan ditampilkan saat tersedia</p>
          </div>
        ) : (
          <div className="space-y-4">
            {selectedCompetitionName && (
              <div className="bg-gradient-ltb-r text-white px-4 py-3 rounded-lg">
                <h2 className="text-lg font-semibold">{selectedCompetitionName}</h2>
              </div>
            )}
            {matches.map((match) => (
              <div key={match.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Match Date Header */}
                <div className="bg-gray-100 px-4 py-2 border-b">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">
                      {formatDate(match.default_match_date || match.match_date)}
                    </span>
                    <span className="text-sm text-ltb-bright font-medium">
                      {formatTime(match.default_match_date || match.match_date)} WIB
                    </span>
                  </div>
                  {match.copa_round_name && (
                    <span className="text-xs text-gray-500">{match.copa_round_name}</span>
                  )}
                </div>
                
                {/* Match Content */}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    {/* Home Team */}
                    <div className="flex-1 text-center">
                      <img 
                        src={match.home_picture_url} 
                        alt={match.home_club_name}
                        className="w-12 h-12 mx-auto object-contain mb-2"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="12" r="10"/></svg>';
                        }}
                      />
                      <p className="text-sm font-medium text-gray-800">{match.home_club_name}</p>
                      <p className="text-xs text-gray-500">{match.home_club_short_name}</p>
                    </div>
                    
                    {/* VS */}
                    <div className="px-4">
                      <span className="text-xl font-bold text-gray-400">VS</span>
                    </div>
                    
                    {/* Away Team */}
                    <div className="flex-1 text-center">
                      <img 
                        src={match.away_picture_url} 
                        alt={match.away_club_name}
                        className="w-12 h-12 mx-auto object-contain mb-2"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="12" r="10"/></svg>';
                        }}
                      />
                      <p className="text-sm font-medium text-gray-800">{match.away_club_name}</p>
                      <p className="text-xs text-gray-500">{match.away_club_short_name}</p>
                    </div>
                  </div>
                  
                  {/* Match Status */}
                  <div className="mt-4 text-center">
                    <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                      {match.match_status || 'Scheduled'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JadwalPage;
